import {
  DragDropFileSelectError,
  MimeType,
} from "@cx-component-library/react-components";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useExploreImage } from "../ExploreImageContext";

export const ALLOWED_UPLOAD_FORMATS = [
  MimeType.PNG,
  MimeType.JPEG,
  MimeType.WEBP,
];
export const MAX_UPLOAD_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Shared drop/error handling for the two `CovUploader` entry points into the
 * explore-image flow: the idle `ImageUploadPanel` and the compact uploader
 * shown in the results sidebar after a reset (CX-2908). Both must apply the
 * exact same validation (size/format) and land on the same
 * `setUploadedImage` call so a dropped file always continues into
 * region-selection the same way, regardless of which uploader triggered it.
 */
export const useImageUpload = () => {
  const { t } = useTranslation();
  const { setUploadedImage } = useExploreImage();
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleFilesDropped = useCallback(
    (fileList: FileList) => {
      const file = fileList[0];
      if (!file) return;

      const imageUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        setUploadedImage(file, imageUrl, {
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
      };
      img.src = imageUrl;
    },
    [setUploadedImage],
  );

  const handleFileError = useCallback(
    (error: DragDropFileSelectError) => {
      switch (error) {
        case DragDropFileSelectError.MAX_FILE_SIZE:
          setToastMessage(t("ldp_explore_image_error_file_too_large"));
          break;
        case DragDropFileSelectError.UN_SUPPORTED_FORMAT:
          setToastMessage(t("ldp_explore_image_error_unsupported_format"));
          break;
      }
    },
    [t],
  );

  return {
    handleFilesDropped,
    handleFileError,
    toastMessage,
    clearToast: () => setToastMessage(null),
  };
};
